// Change pings → TanStack Query invalidation (RT-01; ADR-0017, program §1.2). The API streams
// `GET /v1/companies/{co}/changes` (server-sent events): one `change` per relayed event the
// signed-in principal may read, naming only its subject. Nothing is replicated client-side: a
// ping invalidates the query keys that show that subject, and TanStack refetches the ones on
// screen through the ordinary, permission-filtered routes. Inactive queries are only marked
// stale. Pings that arrive together (one write emits several events) are handled as one batch.
//
// Every key of a company lives under `["company", co, …]` (features/queries.ts, ops-queries.ts),
// so a subject maps to the key roots that render it. A kind with no mapping invalidates the
// whole company: a later module that adds a subject is live before it adds its row here.
//
// CHAT-PERF: a ping on a message carries the message, already filtered for this viewer by the
// server (the list route's read rule). It is written straight into the cached lists of its
// thread (live/cache-patch.ts), at once rather than after the batch window, and the thread's
// lists are not refetched; only what the row does not carry (unread counts, topic order) is
// invalidated, with the batch. Every other ping invalidates as before.
//
// WORK-PERF: a `task.*` ping carries the task card as the list routes serve it to this viewer;
// it is written into every Work cache that shows the task (features/work/task-cache.ts). A
// message ping also lands in a task drawer's conversation when that is its thread.
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { MessageView } from "../features/chat/chat-queries";
import { patchTaskPing, patchThreadMessage } from "../features/work/task-cache";
import type { TaskCard } from "../features/work/view-engine";
import { cachedMessage, patchMessage, updateMessage } from "./cache-patch";

export type ChangePing = {
  subject_kind: string | null;
  subject_id: string | null;
  type: string;
  seq: number;
  /** A message ping's row, as this viewer reads it (CHAT-PERF). */
  message?: MessageView;
  /** A task ping's card, as this viewer's lists serve it (WORK-PERF). */
  task?: TaskCard;
};

/**
 * Writes a payload ping into the cache; returns the keys still to refetch around it, or null
 * when the ping carries nothing to write (it then invalidates by its subject, as any other).
 */
export type PingPatcher = (
  queryClient: QueryClient,
  co: string,
  ping: ChangePing,
) => QueryKey[] | null;

const chat = (co: string, ...rest: string[]): QueryKey => ["company", co, "chat", ...rest];

/** Chat: the message itself, and for a new or removed one the counts and orders around it. */
const patchMessagePing: PingPatcher = (queryClient, co, p) => {
  // COS-SLICE1: a follow-up card's events name the message it sits under; its thread's cards
  // refetch (and the message, when the ping carries it, is written as any other).
  // An edited or deleted message takes its card with it (the server hides it), so they refetch too.
  const cards =
    p.type.startsWith("suggestion.") || p.type === "message.edited" || p.type === "message.deleted";
  // CHAT-THREAD-AGENTS: an agent muted in a thread leaves its header chips (the readiness query).
  if (p.type === "thread.agent_left") return [["company", co, "agent-readiness"]];
  if (!p.message) return cards ? [chat(co, "suggestions")] : null;
  // REPLY-COUNT: a new reply moves its root's count and last-reply time (the root's own cached
  // copy carries the server's real count, which the loaded replies may not).
  const reply = p.message;
  if (
    p.type === "message.created" &&
    reply.parent_message_id &&
    !cachedMessage(queryClient, co, reply.thread_id, reply.id)
  )
    updateMessage(queryClient, co, reply.thread_id, reply.parent_message_id, (root) => ({
      ...root,
      reply_count: (root.reply_count ?? 0) + 1,
      last_reply_at: reply.created_at,
    }));
  patchMessage(queryClient, co, p.message, { insert: p.type === "message.created" });
  patchThreadMessage(queryClient, co, p.message);
  // CHAT-CONTROLS: a reply changes the Threads view's counts and order too.
  if (p.type === "message.created" || p.type === "message.deleted")
    return [
      chat(co, "channels"),
      chat(co, "topics"),
      chat(co, "topic"),
      chat(co, "room"),
      chat(co, "threads"),
      ...(cards ? [chat(co, "suggestions")] : []),
    ];
  if (p.type === "message.bookmark_changed") return [chat(co, "bookmarks")];
  // THREAD-READS: a read or a follow on the root moves the Threads counts (and "n new").
  if (p.type === "thread.read" || p.type === "thread.follow_changed") return [chat(co, "threads")];
  if (cards) return [chat(co, "suggestions")];
  return [];
};

/** Subject kinds whose pings may carry their row. */
export const PATCHERS: Record<string, PingPatcher> = {
  message: patchMessagePing,
  task: patchTaskPing,
};

/** Key roots below `["company", co]` that show each subject kind. */
const ROOTS: Record<string, readonly string[]> = {
  // REPORTS-UI: the caller's own report email reads the company's settings.
  company: ["detail", "report-subscription"],
  seat: ["seats", "seat", "scorecard", "digital-employees", "de", "reports", "org-chart"],
  digital_employee: [
    "digital-employees",
    "de",
    "seats",
    "seat",
    "scorecard",
    "reports",
    "org-chart",
    // AGENT-MENTION: a DE pausing, resuming or going live changes who a mention wakes.
    "agent-readiness",
  ],
  agent_key: ["de"],
  external_identity: ["de", "imports"],
  metric_def: ["metrics", "targets", "observations", "scorecard", "seat", "de", "reports"],
  target_rule: ["targets", "scorecard", "seat", "de", "reports"],
  observation: ["observations", "scorecard", "seat", "de", "reports", "publications"],
  scorecard_publication: ["publications", "scorecard", "seat", "de", "reports"],
  friday_report: ["reports"],
  // WOS-BUNDLE: a task's Time section and its board's total read time entries too.
  time_entry: ["time", "seat", "de", "costs", "work"],
  cost_event: ["costs", "seat", "de"],
  de_subscription: ["de-subscriptions", "de", "seat", "costs"],
  membership: ["people", "seats", "seat", "de", "org-chart", "agent-readiness"],
  invitation: ["people"],
  role: ["roles", "seats", "people"],
  // M1-05, M1-06: responsibilities and their recurring work (seat drawer), templates.
  responsibility: ["responsibilities", "recurring-work"],
  recurring_work: ["recurring-work"],
  template: ["templates"],
  // M1-08: teams and the org chart's lines (Chart mode, Setup › Teams).
  team: ["teams", "org-chart"],
  seat_edge: ["org-chart"],
  // Chat (M2-06): every chat key sits under `chat` (features/chat/chat-queries.ts), so a new
  // message, topic or channel refetches the open conversation and the sidebar's unread pills.
  // BOARD-ORDER: a space's move (and rename) reorders Work's tree as well.
  space: ["chat", "work"],
  channel: ["chat", "agent-readiness"],
  topic: ["chat"],
  message: ["chat"],
  attachment: ["chat"],
  // Work (WORK-PERF): a task ping without its card (a deleted task) refetches Work only.
  task: ["work"],
  // BOARD-ORDER: a board's move, colour or lead (the tree, Work home, the board header).
  board: ["work"],
  // WOS-BUNDLE: a tag renamed, recoloured, merged or deleted: the tags list (`work/tags`) and
  // every card that shows it (Work). Favorites and recents name a deleted tag nowhere.
  tag: ["work"],
  // MEETINGS: the operating cadence (issues board, series, a meeting, the vision page); a
  // decision is logged by a meeting or an issue's solve.
  issue: ["cadence"],
  // AGENT-POLISH: a decision changes its chat card, the approvals lists and the run timeline's
  // held-post badge (the placeholder message has its own `message.edited` ping).
  approval: ["approval", "approvals", "runs"],
  meeting: ["cadence"],
  meeting_series: ["cadence"],
  decision: ["cadence"],
  // M8-ASSESS: rounds, respondents and proposals (Setup › Assessments, Home's card).
  assessment_round: ["assessments"],
  assessment_participant: ["assessments"],
  assessment_proposal: ["assessments"],
  // FOUNDER-OS: a new founder brief refetches the Portfolio views (features/portfolio).
  founder_brief: ["portfolio"],
  // FOUNDER-OS phase 1: a held company's new roll-up (`portfolio_projection.published`, on the
  // portfolio company's stream) refetches Focus, the founder chart, the roll-up grid and Live.
  portfolio_projection: ["portfolio"],
};

/**
 * WOS-BUNDLE: keys named by an event's type, on top of its subject's (a membership-subject event
 * of My Tasks must not refetch People; a timer ping with its card must still reach the timer).
 */
const TYPE_KEYS: Record<string, readonly (readonly string[])[]> = {
  // A person's own arrangement is announced on their membership (live/visibility.ts sends it to
  // them only): just their list refetches, not the people and seats a membership change does.
  "favorite.added": [["work", "favorites"]],
  "favorite.removed": [["work", "favorites"]],
  "favorite.reordered": [["work", "favorites"]],
  "recent.viewed": [["work", "recents"]],
  "task.timer_started": [
    ["work", "timer"],
    ["work", "board"],
  ],
  "task.timer_stopped": [
    ["work", "timer"],
    ["work", "board"],
  ],
  "my_task_section.created": [["work", "my-sections"]],
  "my_task_section.updated": [["work", "my-sections"]],
  "my_task_section.deleted": [["work", "my-sections"]],
  "my_task.placed": [["work", "my-sections"]],
  "board.duplicated": [["work"], ["chat"]],
  "space.duplicated": [["work"], ["chat"]],
  "webhook.paused": [["webhooks"]],
  "webhook.resumed": [["webhooks"]],
  // PARITY P4: an access change that may have cut this membership off something (the API's
  // access.invalidate consumer pings it): Work and Chat refetch, and what is no longer readable
  // answers 404 and leaves the lists.
  "access.changed": [["work"], ["chat"]],
};
/** Subject kinds whose rows the type keys above cover alone (no whole-company refetch). */
const TYPE_ONLY = new Set([
  "favorite.added",
  "favorite.removed",
  "favorite.reordered",
  "recent.viewed",
  "my_task_section.created",
  "my_task_section.updated",
  "my_task_section.deleted",
  "my_task.placed",
  "webhook.paused",
  "webhook.resumed",
  "access.changed",
]);

/** The extra keys a ping's type names (a timer ping's task time is under its task's key). */
export function typeKeys(co: string, p: Pick<ChangePing, "type" | "subject_id">): QueryKey[] {
  const out: QueryKey[] = (TYPE_KEYS[p.type] ?? []).map((k) => ["company", co, ...k]);
  if (p.type.startsWith("task.timer_") && p.subject_id)
    out.push(["company", co, "work", "task", p.subject_id, "time"]);
  return out;
}

/** What an `access.changed` ping drops from the cache (PARITY P4). */
export const revocationKeys = (co: string): QueryKey[] => [
  ["company", co, "work"],
  ["company", co, "chat"],
];

/** Kinds that also change `/v1/me` (company name, roles, memberships). */
const ME_KINDS = new Set(["company", "membership"]);

/** The query keys a batch of pings invalidates (prefixes; deduplicated). */
export function keysForPings(co: string, pings: readonly ChangePing[]): QueryKey[] {
  const out = new Map<string, QueryKey>();
  const add = (k: QueryKey) => out.set(JSON.stringify(k), k);
  for (const p of pings) {
    for (const k of typeKeys(co, p)) add(k);
    if (TYPE_ONLY.has(p.type)) continue;
    const roots = p.subject_kind ? ROOTS[p.subject_kind] : undefined;
    if (!roots) {
      add(["company", co]);
      continue;
    }
    for (const r of roots) add(["company", co, r]);
    if (p.subject_kind && ME_KINDS.has(p.subject_kind)) add(["me"]);
  }
  // A whole-company key covers every narrower one.
  if (out.has(JSON.stringify(["company", co])))
    return [["company", co], ...(out.has('["me"]') ? [["me"]] : [])];
  return [...out.values()];
}

/** What the hook needs of `EventSource` (a fake in tests). */
export type ChangeSource = {
  addEventListener(type: string, listener: (e: MessageEvent) => void): void;
  close(): void;
};

export type ChangeStreamOptions = {
  url: string;
  companyId: string;
  queryClient: QueryClient;
  open?: (url: string) => ChangeSource;
  coalesceMs?: number;
  /** Each batch's keys after invalidation (tests, diagnostics). */
  onInvalidate?: (keys: QueryKey[]) => void;
  /** Each ping written straight into the cache (tests, diagnostics). */
  onPatch?: (ping: ChangePing) => void;
  /** Payload patchers by subject kind (default `PATCHERS`). */
  patchers?: Record<string, PingPatcher>;
};

/** Opens the stream and wires it to the query cache; returns the disconnect function. */
export function connectChangePings(o: ChangeStreamOptions): () => void {
  const open = o.open ?? ((url) => new EventSource(url, { withCredentials: true }));
  const coalesceMs = o.coalesceMs ?? 50;
  const patchers = o.patchers ?? PATCHERS;
  const source = open(o.url);
  let pending: ChangePing[] = [];
  /** Keys around patched rows, refetched with the batch. */
  let around: QueryKey[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let readies = 0;
  let closed = false;

  const invalidate = (keys: QueryKey[]) => {
    for (const queryKey of keys) void o.queryClient.invalidateQueries({ queryKey });
    // The wallet's queries live under ["wallet", co] and usage debits send no ping of their own:
    // any change of the company re-reads an open Billing page (inactive queries just go stale).
    void o.queryClient.invalidateQueries({ queryKey: ["wallet", o.companyId] });
    o.onInvalidate?.(keys);
  };
  const everything = () => invalidate([["company", o.companyId]]);

  source.addEventListener("ready", () => {
    // Every `ready` after the first is a reconnect: pings may have been missed meanwhile.
    if (readies++ > 0) everything();
  });
  source.addEventListener("resync", everything);
  source.addEventListener("change", (e) => {
    let ping: ChangePing;
    try {
      ping = JSON.parse(String(e.data)) as ChangePing;
    } catch {
      return;
    }
    // PARITY P4 (Codex r2 #8): an access change that may have cut this membership off drops
    // what Work and Chat hold now (an invalidation keeps old data on a failed refetch, and
    // keeps inactive data at all); active screens refetch what is still readable.
    if (ping.type === "access.changed" && !closed) {
      for (const queryKey of revocationKeys(o.companyId)) {
        void o.queryClient
          .cancelQueries({ queryKey })
          .then(() => o.queryClient.resetQueries({ queryKey }));
      }
    }
    // A ping that carries its row is written now; only what is around it waits for the batch.
    const patcher = ping.subject_kind ? patchers[ping.subject_kind] : undefined;
    let keys: QueryKey[] | null = null;
    if (patcher && !closed) {
      try {
        keys = patcher(o.queryClient, o.companyId, ping);
      } catch {
        keys = null;
      }
    }
    if (keys) {
      around.push(...keys, ...typeKeys(o.companyId, ping));
      o.onPatch?.(ping);
    } else pending.push(ping);
    timer ??= setTimeout(() => {
      timer = null;
      const batch = pending;
      const extra = around;
      pending = [];
      around = [];
      if (closed) return;
      const out = new Map<string, QueryKey>();
      for (const k of [...(batch.length ? keysForPings(o.companyId, batch) : []), ...extra])
        out.set(JSON.stringify(k), k);
      if (out.size) invalidate([...out.values()]);
    }, coalesceMs);
  });
  source.addEventListener("end", (e) => {
    let reason = "";
    try {
      reason = (JSON.parse(String(e.data)) as { reason?: string }).reason ?? "";
    } catch {}
    // Access is gone: stop reconnecting, and let `/me` decide (the company leaves the switcher;
    // an ended session goes to /login through watchSessionExpiry). `timeout` reconnects.
    if (reason === "revoked" || reason === "unauthenticated") {
      source.close();
      invalidate([["me"]]);
    }
  });

  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    source.close();
  };
}
